import React, { useState, useRef, useCallback, useEffect } from 'react';
import { documents } from '../services/api.js';
import './DocumentUpload.css';

const ACCEPTED_TYPES = '.pdf,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg,.tiff,.doc,.docx';
const MAX_FILE_SIZE = 250 * 1024 * 1024; // 250MB

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

const STATUS_LABELS = {
  pending: 'Pending',
  uploading: 'Uploading...',
  uploaded: 'Uploaded',
  extracting: 'Extracting text...',
  analyzing: 'Analyzing...',
  processed: 'Processed',
  failed: 'Failed',
};

/**
 * DocumentUpload — drag-and-drop file upload component for the DEX Engine
 * document ingestion pipeline.
 *
 * Props:
 *  - submissionId  (string)   Submission to link documents to
 *  - accountId     (string)   Account owning the documents
 *  - onComplete    (function) Called when all files finish processing
 */
export function DocumentUpload({ submissionId, accountId, onComplete }) {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadStates, setUploadStates] = useState({}); // { [fileName]: { status, documentId, error } }
  const [isUploading, setIsUploading] = useState(false);
  const pollIntervalRefs = useRef({});

  // Clean up polling intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollIntervalRefs.current).forEach(clearInterval);
    };
  }, []);

  const handleFiles = useCallback((newFiles) => {
    const fileArray = Array.from(newFiles);
    const valid = fileArray.filter((f) => {
      if (f.size > MAX_FILE_SIZE) {
        alert(`${f.name} exceeds the 250 MB file size limit.`);
        return false;
      }
      return true;
    });
    setFiles((prev) => [...prev, ...valid]);
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleFileSelect = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Poll the document status until processing completes or fails
  const pollDocumentStatus = useCallback(
    (documentId, fileName) => {
      const interval = setInterval(async () => {
        try {
          const result = await documents.get(documentId);
          const doc =
            result.data?.document || result.document || result.data || result;
          const status =
            doc.ProcessingStatus ||
            doc.processingStatus ||
            doc.processing_status ||
            'extracting';

          setUploadStates((prev) => ({
            ...prev,
            [fileName]: { ...prev[fileName], status },
          }));

          if (status === 'processed' || status === 'failed') {
            clearInterval(interval);
            delete pollIntervalRefs.current[fileName];

            // Check whether every file is done
            setUploadStates((prev) => {
              const allDone = Object.values(prev).every(
                (s) => s.status === 'processed' || s.status === 'failed'
              );
              if (allDone && onComplete) {
                setTimeout(() => onComplete(), 500);
              }
              return prev;
            });
          }
        } catch {
          // Keep polling on transient errors
        }
      }, 3000);

      pollIntervalRefs.current[fileName] = interval;
    },
    [onComplete]
  );

  const uploadAll = async () => {
    if (files.length === 0 || !accountId) return;
    setIsUploading(true);

    for (const file of files) {
      const fileName = file.name;
      try {
        // Step 1: Register document and get presigned URL
        setUploadStates((prev) => ({
          ...prev,
          [fileName]: { status: 'uploading', documentId: null, error: null },
        }));

        const uploadResult = await documents.upload({
          accountId,
          submissionId: submissionId || '',
          fileName: file.name,
          fileType: file.type || 'application/octet-stream',
          fileSize: file.size,
          docType: 'application',
        });

        const data = uploadResult.data || uploadResult;
        const documentId = data.documentId;
        const uploadUrl = data.uploadUrl;

        // Step 2: Upload file directly to S3 via presigned URL
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
            'x-amz-server-side-encryption': 'AES256',
          },
        });

        setUploadStates((prev) => ({
          ...prev,
          [fileName]: { status: 'uploaded', documentId, error: null },
        }));

        // Step 3: Trigger processing
        await documents.process(documentId);

        setUploadStates((prev) => ({
          ...prev,
          [fileName]: { status: 'extracting', documentId, error: null },
        }));

        // Step 4: Poll for status updates
        pollDocumentStatus(documentId, fileName);
      } catch (err) {
        setUploadStates((prev) => ({
          ...prev,
          [fileName]: {
            status: 'failed',
            documentId: null,
            error: err.message || 'Upload failed',
          },
        }));
      }
    }

    setIsUploading(false);
  };

  const hasActiveUploads = Object.values(uploadStates).some(
    (s) =>
      s.status !== 'processed' &&
      s.status !== 'failed' &&
      s.status !== 'pending'
  );

  return (
    <div className="du">
      {/* Drop zone */}
      <div
        className={`du__zone ${dragOver ? 'du__zone--active' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="du__zone-icon">
          <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
            <path
              d="M24 6v26m0-26l-8 8m8-8l8 8"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 32v6a4 4 0 004 4h24a4 4 0 004-4v-6"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p className="du__zone-text">
          Drag &amp; drop files here, or click to browse
        </p>
        <p className="du__zone-hint">
          PDF, Excel, CSV, Text, Images, Word — Max 250 MB per file
        </p>
        <input
          ref={fileInputRef}
          type="file"
          className="du__file-input"
          onChange={handleFileSelect}
          multiple
          accept={ACCEPTED_TYPES}
        />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="du__file-list">
          {files.map((file, idx) => {
            const state = uploadStates[file.name];
            const status = state?.status || 'pending';
            const error = state?.error;

            return (
              <div key={`${file.name}-${idx}`} className="du__file-item">
                <div className="du__file-info">
                  <span className="du__file-name">{file.name}</span>
                  <span className="du__file-size">
                    {formatFileSize(file.size)}
                  </span>
                </div>
                <div className="du__file-status-row">
                  <span
                    className={`du__file-status du__file-status--${status}`}
                  >
                    {STATUS_LABELS[status] || status}
                  </span>
                  {error && <span className="du__file-error">{error}</span>}
                  {status === 'pending' && !isUploading && (
                    <button
                      className="du__file-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(idx);
                      }}
                      title="Remove file"
                    >
                      &times;
                    </button>
                  )}
                </div>
                {(status === 'uploading' ||
                  status === 'extracting' ||
                  status === 'analyzing') && (
                  <div className="du__progress-bar">
                    <div
                      className={`du__progress-fill du__progress-fill--${status}`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload button */}
      {files.length > 0 && !hasActiveUploads && (
        <button
          className="btn btn--primary du__upload-btn"
          onClick={uploadAll}
          disabled={isUploading || !accountId}
        >
          {isUploading
            ? 'Uploading...'
            : `Upload ${files.length} file${files.length > 1 ? 's' : ''}`}
        </button>
      )}
    </div>
  );
}
